import React from 'react';
import styles from './PrivacyPolicy.module.css';

const PrivacyPolicy = () => {
  return (
    <div className={styles.privacyPolicyContainer}>
      <div className={styles.privacyPolicyContent}>
        <h1>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last updated: January 30, 2025</p>

        <section className={styles.privacySection}>
          <h2>1. Introduction</h2>
          <p>
            Aurelia Luxury Residences ("we," "our," or "us") respects your privacy and is committed to protecting your personal information 
            in accordance with the Nigeria Data Protection Regulation (NDPR) 2019. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you visit our website, make reservations, or use our services.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information</h3>
          <p>We may collect personal information that you voluntarily provide to us, including:</p>
          <ul>
            <li>Name, email address, phone number</li>
            <li>Billing and payment information</li>
            <li>Booking and reservation details</li>
            <li>Government-issued identification (as required by local laws)</li>
            <li>Emergency contact information</li>
            <li>Preferences and special requests</li>
            <li>Communication preferences</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <p>When you visit our website, we automatically collect certain information:</p>
          <ul>
            <li>IP address and location data</li>
            <li>Browser type and version</li>
            <li>Device information</li>
            <li>Pages visited and time spent on our site</li>
            <li>Referring website information</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>

          <h3>2.3 Cookies and Tracking Technologies</h3>
          <p>
            We use cookies, web beacons, and similar technologies to enhance your experience. 
            You can manage your cookie preferences through our cookie consent banner.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information for the following purposes:</p>
          <ul>
            <li>Processing and managing your reservations</li>
            <li>Providing customer service and support</li>
            <li>Communicating about your bookings and our services</li>
            <li>Processing payments and preventing fraud</li>
            <li>Improving our website and services</li>
            <li>Marketing and promotional activities (with your consent)</li>
            <li>Complying with legal obligations</li>
            <li>Protecting our rights and property</li>
          </ul>
        </section>

        <section className={styles.privacySection}>
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          
          <h3>4.1 Service Providers</h3>
          <p>
            Third-party companies that provide services on our behalf, such as payment processing, 
            email delivery, customer support, and analytics services.
          </p>

          <h3>4.2 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your information may be 
            transferred to the new owner.
          </p>

          <h3>4.3 Legal Requirements</h3>
          <p>
            We may disclose your information when required by law, legal process, or to protect 
            the rights, property, or safety of Aurelia, our users, or others.
          </p>

          <h3>4.4 With Your Consent</h3>
          <p>We may share your information with your explicit consent or at your direction.</p>
        </section>

        <section className={styles.privacySection}>
          <h2>5. Your Rights and Choices</h2>
          
          <h3>5.1 NDPR Rights (Nigerian Residents)</h3>
          <p>Under the Nigeria Data Protection Regulation, you have the following rights:</p>
          <ul>
            <li><strong>Right to Information:</strong> Be informed about how your data is processed</li>
            <li><strong>Right of Access:</strong> Request access to your personal data</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Right to Delete/Erasure:</strong> Request deletion of your personal data</li>
            <li><strong>Right to Object:</strong> Object to processing of your data</li>
            <li><strong>Right to Restrict Processing:</strong> Request restriction of processing</li>
            <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>

          <h3>5.2 Communication Preferences</h3>
          <p>
            You can opt out of marketing communications by clicking the unsubscribe link in our emails 
            or contacting us directly.
          </p>

          <h3>5.3 Cookie Settings</h3>
          <p>
            You can manage your cookie preferences through your browser settings or our cookie consent banner.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the 
            internet or electronic storage is 100% secure.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this 
            Privacy Policy, unless a longer retention period is required or permitted by law.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>8. Legal Basis for Processing</h2>
          <p>
            Under the NDPR, we process your personal data based on the following legal grounds:
          </p>
          <ul>
            <li>Your explicit consent for marketing communications and non-essential services</li>
            <li>Performance of contract when processing booking and reservation data</li>
            <li>Legal obligation for tax records and regulatory compliance</li>
            <li>Legitimate interests for business operations and fraud prevention</li>
          </ul>
        </section>

        <section className={styles.privacySection}>
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than Nigeria. When we transfer your data 
            internationally, we ensure appropriate safeguards are in place, including adequate data protection agreements, 
            to protect your information in accordance with NDPR requirements.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>10. Children's Privacy</h2>
          <p>
            Our services are not directed to children under 18. We do not knowingly collect personal information from 
            children under 18. If we become aware that we have collected such information, we will delete it promptly.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>10. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the privacy practices 
            of these external sites. We encourage you to review their privacy policies.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
            the new policy on our website and updating the "Last updated" date.
          </p>
        </section>

        <section className={styles.privacySection}>
          <h2>12. Contact Information</h2>
          <p>If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:</p>
          
          <div className={styles.contactInfo}>
            <div className={styles.contactItem}>
              <strong>Email:</strong> info@aurelia-apartments.com
            </div>
            <div className={styles.contactItem}>
              <strong>Phone:</strong> +2348165028212
            </div>
            <div className={styles.contactItem}>
              <strong>Address:</strong> 2 Bukar Dipcharima close off Teslim Elias street off TOS Benson Street Utako Abuja, Nigeria
            </div>
          </div>
        </section>

        <section className={styles.privacySection}>
          <h2>14. Data Protection Authority</h2>
          <p>
            For NDPR-related inquiries, you can contact our Data Protection Officer at: 
            <strong> info@aurelia-apartments.com</strong>
          </p>
          <p>
            If you believe we have not adequately resolved your privacy concerns, you have the right to lodge a complaint 
            with the National Information Technology Development Agency (NITDA), Nigeria's data protection regulatory authority.
          </p>
          <div className={styles.contactInfo}>
            <div className={styles.contactItem}>
              <strong>NITDA Contact:</strong> info@nitda.gov.ng
            </div>
            <div className={styles.contactItem}>
              <strong>Website:</strong> https://nitda.gov.ng
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
